import React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useAuth } from '../hooks/useAuth';
import { getHelpdeskUrl } from '../lib/helpdesk-utils';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Logo } from '@/components/Logo';
import { Building2, Users, LogOut, ArrowRight, Crown, UserCheck } from 'lucide-react';

export const OrganizationSelector: React.FC = () => {
  const { user, availableOrganizations, logout } = useAuth();
  const navigate = useNavigate();

  const handleOrganizationSelect = () => {
    // With unified multi-org view, we go directly to helpdesk dashboard
    // The user will see all their organizations in the unified view
    navigate({ to: getHelpdeskUrl.dashboard() });
  };

  const handleLogout = async () => {
    await logout();
    navigate({ to: '/login' });
  };

  const getUserRoleInOrganization = (organizationId: string) => {
    const userOrg = user?.userOrganizations?.find(
      uo => uo.organizationId === organizationId && uo.isActive
    );
    return userOrg?.role || 'Member';
  };

  const getRoleIcon = (role: string) => {
    return role === 'Admin' ? Crown : UserCheck;
  };

  const getRoleBadgeVariant = (role: string) => {
    return role === 'Admin' ? 'default' : 'secondary';
  };

  if (!user || availableOrganizations.length === 0) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
      <div className="container mx-auto px-4 py-6 sm:py-8 max-w-6xl">
        {/* Header */}
        <div className="text-center mb-6 sm:mb-10 md:mb-12">
          <div className="flex justify-center mb-4 sm:mb-6 md:mb-8">
            <Logo className="h-16 sm:h-20 md:h-24" />
          </div>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-2 sm:mb-3">
            Welcome back, {user.firstName}
          </h1>
          <p className="text-slate-600 dark:text-slate-400 text-sm sm:text-base md:text-lg">
            Choose an organization to access your helpdesk
          </p>
        </div>

        {/* User Profile Section */}
        <div className="flex flex-col sm:flex-row justify-between items-center mb-6 sm:mb-10 md:mb-12 bg-white dark:bg-slate-800 rounded-xl sm:rounded-2xl p-4 sm:p-6 shadow-sm border border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-0">
            <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center shadow-lg flex-shrink-0">
              <span className="text-white font-semibold text-base sm:text-lg">
                {user.firstName?.charAt(0) || user.email.charAt(0)}
              </span>
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-slate-900 dark:text-white text-base sm:text-lg truncate">
                {user.firstName} {user.lastName}
              </p>
              <p className="text-slate-600 dark:text-slate-400 text-sm sm:text-base truncate">
                {user.email}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            onClick={handleLogout}
            size="lg"
            className="w-full sm:w-auto transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 hover:border-red-300 hover:text-red-600 dark:hover:border-red-600 dark:hover:text-red-400"
          >
            <LogOut className="h-4 w-4 mr-2 transition-transform duration-300 group-hover:scale-110" />
            Sign Out
          </Button>
        </div>

        {/* Organizations Grid */}
        <div className="grid gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {availableOrganizations.map((org) => {
            const userRole = getUserRoleInOrganization(org.id);
            const RoleIcon = getRoleIcon(userRole);

            return (
              <Card
                key={org.id}
                className="group cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-1 hover:border-blue-400 dark:hover:border-blue-500 border-2 border-transparent bg-white dark:bg-slate-800 h-full"
                onClick={() => handleOrganizationSelect()}
              >
                <div className="p-4 sm:p-6 flex flex-col h-full">
                  {/* Organization Header */}
                  <div className="flex items-start justify-between mb-3 sm:mb-4">
                    <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-900 dark:to-blue-800 rounded-lg sm:rounded-xl flex items-center justify-center shadow-md group-hover:scale-105 transition-transform duration-300 flex-shrink-0">
                        <Building2 className="h-6 w-6 sm:h-7 sm:w-7 md:h-8 md:w-8 text-blue-600 dark:text-blue-400" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base sm:text-lg md:text-xl font-semibold text-slate-900 dark:text-white mb-1.5 sm:mb-2 truncate">
                          {org.name}
                        </h3>
                        <Badge
                          variant={getRoleBadgeVariant(userRole)}
                          className="text-xs flex items-center gap-1 w-fit"
                        >
                          <RoleIcon className="h-3 w-3" />
                          {userRole}
                        </Badge>
                      </div>
                    </div>
                    <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5 text-slate-400 group-hover:text-blue-500 group-hover:translate-x-1 transition-all duration-300 flex-shrink-0 mt-1 sm:mt-2" />
                  </div>

                  {/* Organization Details */}
                  <div className="mt-auto pt-3 sm:pt-4 border-t border-slate-100 dark:border-slate-700">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-slate-600 dark:text-slate-400 font-medium text-sm sm:text-base">
                        Access Helpdesk
                      </span>
                      <div className="flex items-center gap-2">
                        <code className="text-xs bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 px-2 py-1 rounded font-mono truncate max-w-[120px] sm:max-w-none">
                          {org.subdomain}
                        </code>
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>

        {/* Footer */}
        <div className="mt-8 sm:mt-10 md:mt-12 text-center">
          <div className="inline-flex items-center gap-2 bg-white dark:bg-slate-800 rounded-full px-4 sm:px-6 py-2 sm:py-3 shadow-sm border border-slate-200 dark:border-slate-700">
            <Users className="h-4 w-4 text-slate-500 flex-shrink-0" />
            <span className="text-slate-600 dark:text-slate-400 font-medium text-xs sm:text-sm">
              {availableOrganizations.length} organization{availableOrganizations.length !== 1 ? 's' : ''} available
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};